import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Clock, Image, Calendar, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

const PendingMilestoneReviews = ({ onUpdate }) => {
  const [pendingMilestones, setPendingMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingMilestones();
  }, []);

  const fetchPendingMilestones = async () => {
    try {
      const res = await api.get('/milestones/pending-review');
      setPendingMilestones(res.data);
    } catch (error) {
      console.error('Failed to fetch pending milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (milestone) => {
    setSelectedMilestone(milestone);
    setFeedback('');
    setShowReviewModal(true);
  };

  const submitReview = async (action) => {
    if (!selectedMilestone) return;
    
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('action', action);
      formData.append('feedback', feedback || '');

      await api.post(`/milestones/${selectedMilestone.milestone_id}/admin-review`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Milestone ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowReviewModal(false);
      setSelectedMilestone(null);
      setFeedback('');
      fetchPendingMilestones();
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} milestone`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-8 text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground mt-2 text-sm">Loading pending reviews...</p>
        </CardContent>
      </Card>
    );
  }

  if (pendingMilestones.length === 0) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-muted-foreground">No milestone completions pending review</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Milestone Completions Pending Review</h3>
          <Badge className="bg-amber-100 text-amber-700">{pendingMilestones.length} pending</Badge>
        </div>
        
        {pendingMilestones.map((item, index) => (
          <motion.div
            key={item.milestone.milestone_id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground">{item.milestone.name}</h4>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        {item.milestone.weightage}%
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {item.project?.project_name || 'Unknown Project'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(item.milestone.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                    {item.milestone.completion_remarks && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        "{item.milestone.completion_remarks}"
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.milestone.completion_photo && (
                      <a 
                        href={item.milestone.completion_photo.startsWith('/') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${item.milestone.completion_photo}`
                          : item.milestone.completion_photo
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Image className="w-4 h-4" />
                        View Photo
                      </a>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleReview(item.milestone)}
                      className="bg-primary hover:bg-primary/90"
                    >
                      Review
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-md bg-white dark:bg-stone-900 border-0 shadow-2xl rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Review Milestone Completion</DialogTitle>
            <DialogDescription>
              {selectedMilestone?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Milestone Details */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Weightage</span>
                <span className="font-medium">{selectedMilestone?.weightage}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Completed</span>
                <span className="font-medium">
                  {selectedMilestone?.completed_at && new Date(selectedMilestone.completed_at).toLocaleDateString()}
                </span>
              </div>
              {selectedMilestone?.completion_remarks && (
                <div className="pt-2 border-t border-border">
                  <p className="text-sm text-muted-foreground">Engineer's Remarks:</p>
                  <p className="text-sm mt-1">"{selectedMilestone.completion_remarks}"</p>
                </div>
              )}
            </div>

            {/* Completion Photo */}
            {selectedMilestone?.completion_photo && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Completion Photo</p>
                <img
                  src={selectedMilestone.completion_photo.startsWith('/')
                    ? `${process.env.REACT_APP_BACKEND_URL}${selectedMilestone.completion_photo}`
                    : selectedMilestone.completion_photo
                  }
                  alt="Milestone completion"
                  className="w-full h-48 object-cover rounded-lg border border-border"
                />
              </div>
            )}

            {/* Feedback */}
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Feedback (Optional)</p>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Add feedback for the engineer..."
                className="min-h-[80px] resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={() => submitReview('reject')}
                disabled={submitting}
                variant="outline"
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={() => submitReview('approve')}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PendingMilestoneReviews;
